/**
 * Универсальный сканер кодов оборудования (терминалы, роутеры).
 *
 * На этикетке устройства обычно несколько кодов: QR и линейные штрих-коды для
 * серийного номера, MAC, GPON-SN и т.п. Нужен именно серийный номер, поэтому:
 *
 *  1. с одного снимка распознаём ВСЕ коды (BarcodeDetector.detect);
 *  2. ориентируемся на метки S/N, SN, Serial — если код содержит такую метку,
 *     вытаскиваем значение после неё; такой кандидат считаем серийным номером;
 *  3. коды без метки остаются кандидатами «на выбор» — монтажник выбирает сам.
 *
 * Почему по снимку, а не с «живой» камеры: BarcodeDetector.detect() принимает
 * готовую картинку, и снимок из `<input type="file" capture>` работает даже по
 * http:// в локальной сети, где getUserMedia недоступен (см. lib/camera).
 */

// BarcodeDetector ещё не в стандартных d.ts TypeScript — описываем минимально.
interface DetectedBarcode {
  rawValue: string
  format: string
}
interface BarcodeDetectorLike {
  detect(source: ImageBitmapSource): Promise<DetectedBarcode[]>
}
type BarcodeDetectorCtor = {
  new (options?: { formats?: string[] }): BarcodeDetectorLike
  getSupportedFormats?: () => Promise<string[]>
}

// Форматы на этикетках оборудования: линейные (Code 128/39, EAN, ITF) и 2D.
const WANTED_FORMATS = [
  'qr_code',
  'code_128',
  'code_39',
  'code_93',
  'codabar',
  'ean_13',
  'ean_8',
  'itf',
  'data_matrix',
  'pdf417',
  'aztec',
]

// Метка серийного номера и значение после неё. Ловит «SN», «S/N», «S\N»,
// «Serial», «Serial No», «Serial Number», с разделителем «: # =» или пробелом.
const SERIAL_MARKER =
  /(?:s\s*[/\\]?\s*n|serial(?:\s*(?:no\.?|number|#))?)\b\s*[:#=]?\s*([A-Za-z0-9][A-Za-z0-9._-]{3,})/i

export interface BarcodeCandidate {
  /** Значение, которое подставится в поле (серийник или весь код). */
  value: string
  /** true — значение вытащено по метке S/N, это серийный номер. */
  isSerial: boolean
  /** Исходный распознанный код (для подсказки, если отличается от value). */
  raw: string
}

function detectorCtor(): BarcodeDetectorCtor | null {
  const ctor = (globalThis as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector
  return ctor ?? null
}

export function isBarcodeScanningSupported(): boolean {
  return detectorCtor() !== null
}

/** Вытаскивает серийный номер из значения кода по метке S/N. null — метки нет. */
export function extractSerial(raw: string): string | null {
  const match = SERIAL_MARKER.exec(raw)
  return match?.[1] ?? null
}

/** Распознаёт все коды на снимке. Пустой массив — ничего не найдено/не поддержано. */
export async function scanBarcodesFromFile(file: File): Promise<string[]> {
  const Ctor = detectorCtor()
  if (!Ctor) return []

  let bitmap: ImageBitmap | null = null
  try {
    let formats = WANTED_FORMATS
    if (Ctor.getSupportedFormats) {
      const supported = await Ctor.getSupportedFormats()
      const narrowed = WANTED_FORMATS.filter((f) => supported.includes(f))
      if (narrowed.length > 0) formats = narrowed
    }
    const detector = new Ctor({ formats })
    bitmap = await createImageBitmap(file)
    const codes = await detector.detect(bitmap)
    return codes.map((c) => c.rawValue.trim()).filter(Boolean)
  } catch {
    return []
  } finally {
    bitmap?.close()
  }
}

/**
 * Превращает набор распознанных кодов в кандидатов, серийные — первыми.
 *
 * Код с меткой S/N даёт серийного кандидата (значение после метки). Коды без
 * метки идут как есть — из них монтажник выберет нужный. Дубли убираются.
 */
export function serialCandidates(values: string[]): BarcodeCandidate[] {
  const out: BarcodeCandidate[] = []
  const seen = new Set<string>()

  // Сначала — значения с явной меткой S/N.
  for (const raw of values) {
    const serial = extractSerial(raw)
    if (serial && !seen.has(serial)) {
      seen.add(serial)
      out.push({ value: serial, isSerial: true, raw })
    }
  }
  // Затем — коды без метки (и не совпавшие с уже вытащенным серийником).
  for (const raw of values) {
    const trimmed = raw.trim()
    if (trimmed && !seen.has(trimmed) && extractSerial(raw) === null) {
      seen.add(trimmed)
      out.push({ value: trimmed, isSerial: false, raw: trimmed })
    }
  }
  return out
}
